<template>
  <div>
    <!-- Başlık + dönem seçici -->
    <div class="d-flex align-center justify-space-between flex-wrap gap-3 mb-5">
      <h4 class="mb-0">Kullanım İstatistikleri</h4>
      <div class="d-flex align-center flex-wrap gap-2">
        <v-btn-toggle
          v-model="period"
          mandatory
          color="primary"
          variant="outlined"
          density="compact"
          @update:model-value="onPeriodChange"
        >
          <v-btn value="today">Bugün</v-btn>
          <v-btn value="yesterday">Dün</v-btn>
          <v-btn value="week">Son 7 Gün</v-btn>
          <v-btn value="month">Son 30 Gün</v-btn>
          <v-btn value="custom">Özel</v-btn>
        </v-btn-toggle>

        <template v-if="period === 'custom'">
          <v-text-field
            v-model="customFrom"
            type="date"
            density="compact"
            variant="outlined"
            hide-details
            style="width: 150px"
            :max="customTo || today"
            @update:model-value="onCustomRangeChange"
          />
          <span class="text-medium-emphasis">—</span>
          <v-text-field
            v-model="customTo"
            type="date"
            density="compact"
            variant="outlined"
            hide-details
            style="width: 150px"
            :min="customFrom"
            :max="today"
            @update:model-value="onCustomRangeChange"
          />
        </template>
      </div>
    </div>

    <!-- Hata -->
    <v-alert v-if="error" type="error" class="mb-4" closable @click:close="error = null">
      {{ error }}
    </v-alert>

    <!-- Loading -->
    <template v-if="loading">
      <v-row>
        <v-col v-for="n in 3" :key="n" cols="12" md="4">
          <v-skeleton-loader type="card" />
        </v-col>
      </v-row>
      <v-row class="mt-2">
        <v-col v-for="n in 2" :key="n" cols="12" md="6">
          <v-skeleton-loader type="card" height="300" />
        </v-col>
      </v-row>
    </template>

    <!-- İçerik -->
    <template v-else-if="stats">
      <StatsKpiCards
        :questions="stats.kpis.questions"
        :tokens="stats.kpis.tokens"
        :rating="stats.kpis.rating"
      />

      <v-row class="mt-4">
        <v-col cols="12" md="6">
          <StatsQuestionsChart :timeseries="stats.question_timeseries" />
        </v-col>
        <v-col cols="12" md="6">
          <StatsTokenChart :timeseries="stats.token_timeseries" />
        </v-col>
      </v-row>

      <v-row class="mt-4">
        <v-col cols="12" lg="8">
          <StatsTokenConsumers :breakdown="stats.user_token_breakdown" />
        </v-col>
        <v-col cols="12" lg="4">
          <StatsRatingChart
            :distribution="stats.rating_distribution"
            :avg-rating="stats.kpis.rating"
          />
        </v-col>
      </v-row>

      <v-row class="mt-4">
        <v-col cols="12">
          <StatsRecentActivity
            :recent-questions="stats.recent_activity.recent_questions"
            :low-rated="stats.recent_activity.low_rated"
            :recent-comments="stats.recent_activity.recent_comments"
          />
        </v-col>
      </v-row>
    </template>
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref } from 'vue'
import { fetchAdminStats, type StatsPeriod, type StatsResponse } from '@/services/adminStatsApi'
import StatsKpiCards from '@/components/Dashboard/Stats/StatsKpiCards.vue'
import StatsQuestionsChart from '@/components/Dashboard/Stats/StatsQuestionsChart.vue'
import StatsTokenChart from '@/components/Dashboard/Stats/StatsTokenChart.vue'
import StatsTokenConsumers from '@/components/Dashboard/Stats/StatsTokenConsumers.vue'
import StatsRatingChart from '@/components/Dashboard/Stats/StatsRatingChart.vue'
import StatsRecentActivity from '@/components/Dashboard/Stats/StatsRecentActivity.vue'

const today = new Date().toISOString().slice(0, 10)

const period    = ref<StatsPeriod>('month')
const customFrom = ref<string>(today)
const customTo   = ref<string>(today)
const loading   = ref(false)
const error     = ref<string | null>(null)
const stats     = ref<StatsResponse | null>(null)

async function loadStats() {
  loading.value = true
  error.value = null
  try {
    const dateRange = period.value === 'custom'
      ? { from: customFrom.value, to: customTo.value }
      : undefined
    const res = await fetchAdminStats(period.value, dateRange)
    stats.value = res.data
  } catch (e: any) {
    error.value = e?.response?.data?.message ?? e?.message ?? 'Bir hata oluştu'
  } finally {
    loading.value = false
  }
}

function onPeriodChange() {
  if (period.value !== 'custom') {
    loadStats()
  }
}

function onCustomRangeChange() {
  if (customFrom.value && customTo.value && customFrom.value <= customTo.value) {
    loadStats()
  }
}

onMounted(loadStats)
</script>
